import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { CreationDocument, RenameDocument } from 'src/app/utils/interfaces/new/CreationDocument';
import { Document } from 'src/app/utils/interfaces/new/Document';
import { DocumentMetadata } from 'src/app/utils/interfaces/new/DocumentMetadata';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { FiltresService } from '../filtres/filtres.service';
import { environment } from 'src/environments/environment';
import { MultiprofessionalProtocolDocumentMetadata } from 'src/app/utils/interfaces/new/MultiprofessionalProtocolDocumentMetadata';
import { UploadGedDocument } from 'src/app/utils/interfaces/new/UploadGEDDocument';
import { SnackbarService } from '../snackbar/snackbar.service';
import { MessageReleveBio } from 'src/app/utils/interfaces/new/MessageReleveBio';
import { MessageReleveBioHTML } from 'src/app/utils/interfaces/new/MessageReleveBioHTML';

const DOCUMENT_PATH = '/document/';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  private basePath = environment.basePath + DOCUMENT_PATH;

  constructor(
    private _http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService
  ) { }

  public async searchDocuments(patientId: string, plainText?: string,
    dateStart?: Date, dateEnd?: Date,
    filtre: Filtres = {
      currentPage: 1, perpage: 10
    }): Promise<Paginated<DocumentMetadata>> {
    return new Promise<Paginated<DocumentMetadata>>(async (resolve, reject) => {
      try {
        this.httpOptions.headers = this._filtresService.setHeaders(filtre);

        const query = {};

        if (plainText && plainText !== '') {
          query['title'] = plainText;
        }

        if (dateStart) {
          query['dateStart'] = encodeURIComponent(moment.utc(dateStart).toISOString());
        }

        if (dateEnd) {
          query['dateEnd'] = encodeURIComponent(moment.utc(dateEnd).toISOString());
        }

        this.httpOptions.params = query;

        const res = await this._http.get(`${this.basePath}patient/${patientId}`, this.httpOptions).toPromise();
        const documents = res.body as Array<DocumentMetadata>;
        const paginated: Paginated<DocumentMetadata> = {
          page: Number(res.headers.get('x-page')),
          perpage: Number(res.headers.get('x-per-page')),
          total: Number(res.headers.get('x-total')),
          objects: documents
        };
        resolve(paginated);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async getDocumentsWithoutConsultation(patientId: string): Promise<Array<DocumentMetadata>> {
    return new Promise<Array<DocumentMetadata>>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        this.httpOptions.headers = this._filtresService.setHeaders({
          currentPage: 1, perpage: -1
        });

        const query = {};
        query['noConsultation'] = 'true';
        this.httpOptions.params = query;

        const res = await this._http.get<Array<DocumentMetadata>>(`${this.basePath}patient/${patientId}`, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async getConsultationDocuments(patientId: string, filtre: Filtres = { currentPage: 1, perpage: 10 }): Promise<Paginated<DocumentMetadata>> {
    return new Promise<Paginated<DocumentMetadata>>(async (resolve, reject) => {
      try {
        this.httpOptions.headers = this._filtresService.setHeaders(filtre);
        this.httpOptions.params = undefined;

        const res = await this._http.get<Array<DocumentMetadata>>(`${this.basePath}consultation/${patientId}`, this.httpOptions).toPromise();
        const documents = res.body as Array<DocumentMetadata>;
        const paginated: Paginated<DocumentMetadata> = {
          page: Number(res.headers.get('x-page')),
          perpage: Number(res.headers.get('x-per-page')),
          total: Number(res.headers.get('x-total')),
          objects: documents
        };
        resolve(paginated);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async getProtocolesDocuments(
    filtre: Filtres = { currentPage: 1, perpage: 10 }
  ): Promise<Paginated<MultiprofessionalProtocolDocumentMetadata>> {
    return new Promise<Paginated<MultiprofessionalProtocolDocumentMetadata>>(async (resolve, reject) => {
      try {
        this.httpOptions.headers = this._filtresService.setHeaders(filtre);
        this.httpOptions.params = undefined;

        const res = await this._http.get<Array<MultiprofessionalProtocolDocumentMetadata>>(
          `${this.basePath}multiprofessional-protocol`,
          this.httpOptions
        ).toPromise();
        const documents = res.body as Array<MultiprofessionalProtocolDocumentMetadata>;
        const paginated: Paginated<MultiprofessionalProtocolDocumentMetadata> = {
          page: Number(res.headers.get('x-page')),
          perpage: Number(res.headers.get('x-per-page')),
          total: Number(res.headers.get('x-total')),
          objects: documents
        };
        resolve(paginated);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async createProtocolDocument(protocolId: string, document: CreationDocument): Promise<MultiprofessionalProtocolDocumentMetadata> {
    return new Promise<MultiprofessionalProtocolDocumentMetadata>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.post<MultiprofessionalProtocolDocumentMetadata>(
          `${this.basePath}multiprofessional-protocol/${protocolId}`,
          document,
          this.httpOptions
        ).toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async uploadFile(file: UploadGedDocument): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.post<string>(
          `${this.basePath}upload`,
          file,
          this.httpOptions
        ).toPromise();

        const warning = res.headers?.get('x-sanitization-warning');
        if (warning) {
          this._snack.openSnackBar(decodeURIComponent(warning));
        }

        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async createDocumentDryRun(patientId: string, document: Array<CreationDocument>): Promise<Array<CreationDocument>> {
    return new Promise<Array<CreationDocument>>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.post<Array<CreationDocument>>(`${this.basePath}patient/${patientId}/dry-run`, document, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async createDocument(patientId: string, document: CreationDocument): Promise<DocumentMetadata> {
    return new Promise<DocumentMetadata>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.post<DocumentMetadata>(`${this.basePath}patient/${patientId}`, document, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async updateDocument(documentId: string, document: CreationDocument, doForce = false): Promise<DocumentMetadata> {
    return new Promise<DocumentMetadata>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;

        if (doForce) {
          const query = {};
          query['doForce'] = 'true';
          this.httpOptions.params = query;
        }

        const res = await this._http.put<DocumentMetadata>(`${this.basePath}document/${documentId}`, document, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async renameDocument(documentId: string, newFilename: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        await this._http.put<DocumentMetadata>(`${this.basePath}document/${documentId}/rename`, { fileName: newFilename } as RenameDocument, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async deleteDocument(documentId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        this.httpOptions.headers = undefined;

        const res = await this._http.delete(`${this.basePath}document/${documentId}`, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async downloadDocument(documentId: string): Promise<Document> {
    return new Promise<Document>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        this.httpOptions.headers = undefined;

        const res = await this._http.get<Document>(`${this.basePath}document/${documentId}`, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async getDocumentDMPTypes(): Promise<Array<DocumentType>> {
    return new Promise<Array<DocumentType>>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;

        const res = await this._http.get<Array<DocumentType>>(`${this.basePath}types`, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public convertDocumentToCreationDocument(document: DocumentMetadata): CreationDocument {
    return {
      contentType: document.contentType,
      fileName: document.fileName,
      gedId: document.gedId,
      titre: document.titre,
      typeId: document.type.id,
      consultationId: document.consultation ? document.consultation.id : undefined,
      authorId: document.auteurCreation.id,
      weight: document.weight,
      isPrivate: document.isPrivate
    };
  }

  public convertFileToBase64(file: File, conservePrefix = false): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.readAsDataURL(file);

        reader.onload = () => {
          resolve(
            conservePrefix
              ? reader.result as string
              : (reader.result as string).split('base64,')[1]
          );
        };

        reader.onerror = (e) => {
          throw new Error();
        };
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public static prefixBase64(base64String: string, contentType?: string) {
    let prefix = "base64,"
    if (contentType) {
      prefix = `data:${contentType};${prefix}`
    }
    let finalBase64 = base64String;
    if (!base64String.startsWith(prefix)) {
      finalBase64 = prefix + finalBase64;
    }
    return finalBase64;
  }

  public async getDocumentByDocumentAssocieId(documentAssocieId: string): Promise<DocumentMetadata | undefined> {
    return new Promise<DocumentMetadata>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.get<DocumentMetadata>(`${this.basePath}documentAssocie/${documentAssocieId}`, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e?.error?.code && (e.error.code === '404' || e.error.code === 404)) {
          resolve(undefined);
        } else {
          this._snack.openSnackBar(e.error.message);
          reject(e);
        }
      }
    });
  }

  public async getCrBioDonneesByDocumentId(documentId: string): Promise<MessageReleveBio | undefined> {
    return new Promise<MessageReleveBio>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.get<MessageReleveBio>(`${this.basePath}cr-bio-data/${documentId}`, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e?.error?.code && (e.error.code === '404' || e.error.code === 404)) {
          resolve(undefined);
        } else {
          this._snack.openSnackBar(e.error.message);
          reject(e);
        }
      }
    });
  }

  public async getCrBioDonneesHtmlByDocumentId(documentId: string): Promise<MessageReleveBioHTML | undefined> {
    return new Promise<MessageReleveBioHTML | undefined>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.get<MessageReleveBioHTML>(`${this.basePath}cr-bio-data/${documentId}/html`, this.httpOptions).toPromise();
        resolve(res.body.apercuHTML ? res.body : undefined);
      } catch (e) {
        if (e?.error?.code && (e.error.code === '404' || e.error.code === 404)) {
          resolve(undefined);
        } else {
          this._snack.openSnackBar(e.error.message);
          reject(e);
        }
      }
    });
  }

  public async convertXmlToHtml(xml: string): Promise<string> {
    return new Promise<string>(async (resolve, reject) => {
      try {
        this.httpOptions.params = undefined;
        const res = await this._http.post<string>(`${this.basePath}convert-xml-to-html`, { data: xml }, this.httpOptions).toPromise();
        resolve(res.body);
      } catch (e) {
        if (e?.error?.code && (e.error.code === '404' || e.error.code === 404)) {
          resolve(undefined);
        } else {
          this._snack.openSnackBar(e.error.message);
          reject(e);
        }
      }
    });
  }

  public async getDetachedDocuments(filtre: Filtres = { currentPage: 1, perpage: 10 }): Promise<Array<DocumentMetadata>> {
    try {
      this.httpOptions.headers = this._filtresService.setHeaders(filtre);

      const res = await this._http.get<Array<DocumentMetadata>>(`${this.basePath}detached`, this.httpOptions).toPromise();
      return res.body;
    } catch (e) {
      throw e;
    }
  }
}
